/**
 * AdminRentalCarImportDialog — admin tool to bulk-import a vendor's
 * fleet from their legacy RentalCar.al author page.
 *
 * Iter 69 — wraps the backend endpoints already shipped in
 * `routes/admin_import_external.py`:
 *
 *   POST /api/admin/import/rentalcar-al/preview  → dry-run, returns
 *        list of cars + flags `already_imported` per car.
 *   POST /api/admin/import/rentalcar-al          → commit. If
 *        `selected_urls` is supplied, only those rows are imported.
 *
 * Flow:
 *   1. Admin pastes the author URL (e.g. https://rentalcar.al/author/omaris/)
 *      + target vendor email.
 *   2. Click "Preview" → backend scrapes the page, returns a table.
 *   3. Each car has a checkbox (already-imported rows pre-disabled).
 *   4. Click "Import N cars" → backend creates/updates each one,
 *      auto-creating the vendor account if it doesn't exist.
 *   5. Result toast + summary modal.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Download, AlertCircle, CheckCircle2 } from "lucide-react";

export default function AdminRentalCarImportDialog() {
  const [open, setOpen] = useState(false);
  const [authorUrl, setAuthorUrl] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // { count, cars, target_vendor_exists }
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);

  const resetAll = () => {
    setAuthorUrl("");
    setVendorEmail("");
    setVendorName("");
    setPreview(null);
    setSelected(new Set());
    setResult(null);
  };

  const runPreview = async () => {
    if (!authorUrl.trim() || !vendorEmail.trim()) {
      toast.error("Author URL and vendor email are required");
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const r = await api.post("/admin/import/rentalcar-al/preview", {
        author_url: authorUrl.trim(),
        target_vendor_email: vendorEmail.trim().toLowerCase(),
        target_vendor_name: vendorName.trim() || undefined,
      });
      setPreview(r.data);
      // Default to selecting every NOT-already-imported car.
      const fresh = new Set(
        (r.data.cars || [])
          .filter((c) => !c.already_imported)
          .map((c) => c.external_source_url)
      );
      setSelected(fresh);
      if ((r.data.cars || []).length === 0) {
        toast.info("Scraper returned 0 cars — verify the author URL");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleOne = (url) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (!preview) return;
    if (!checked) {
      setSelected(new Set());
      return;
    }
    const all = new Set(
      preview.cars.filter((c) => !c.already_imported).map((c) => c.external_source_url)
    );
    setSelected(all);
  };

  const runImport = async () => {
    if (!preview || selected.size === 0) {
      toast.error("Pick at least one car to import");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/admin/import/rentalcar-al", {
        author_url: authorUrl.trim(),
        target_vendor_email: vendorEmail.trim().toLowerCase(),
        target_vendor_name: vendorName.trim() || undefined,
        selected_urls: Array.from(selected),
      });
      setResult(r.data);
      const created = (r.data.created || []).length;
      const updated = (r.data.updated || []).length;
      const errors = (r.data.errors || []).length;
      toast.success(
        `Imported ${created} new · updated ${updated} · ${errors} errors`,
        { duration: 6000 }
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const selectableCount = preview
    ? preview.cars.filter((c) => !c.already_imported).length
    : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          // Wipe state on close so the next open is a clean slate.
          setTimeout(resetAll, 200);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
          data-testid="admin-open-rentalcar-import"
        >
          <Download className="h-3.5 w-3.5" />
          Import from RentalCar.al
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="admin-rentalcar-import-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-border">
          <DialogTitle className="text-xl font-display">Import vendor fleet from RentalCar.al</DialogTitle>
          <DialogDescription>
            Paste the legacy WordPress author page URL (e.g.{" "}
            <code className="text-xs bg-neutral-100 px-1 rounded">https://rentalcar.al/author/omaris/</code>) and the target vendor email. We&apos;ll scrape their listings and create or update matching cars on this platform.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {/* Step 1: form */}
          <div className="space-y-3 pt-4">
            <div>
              <Label htmlFor="rcal-author-url" className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Legacy author URL</Label>
              <Input
                id="rcal-author-url"
                value={authorUrl}
                onChange={(e) => setAuthorUrl(e.target.value)}
                placeholder="https://rentalcar.al/author/omaris/"
                className="mt-1"
                data-testid="rcal-author-url"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rcal-vendor-email" className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Target vendor email</Label>
                <Input
                  id="rcal-vendor-email"
                  type="email"
                  value={vendorEmail}
                  onChange={(e) => setVendorEmail(e.target.value)}
                  placeholder="omaris@example.com"
                  className="mt-1"
                  data-testid="rcal-vendor-email"
                />
              </div>
              <div>
                <Label htmlFor="rcal-vendor-name" className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Business name <span className="text-neutral-400 font-normal lowercase">(optional)</span></Label>
                <Input
                  id="rcal-vendor-name"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Omaris Car Rental"
                  className="mt-1"
                  data-testid="rcal-vendor-name"
                />
              </div>
            </div>
            <div>
              <Button
                onClick={runPreview}
                disabled={previewing}
                className="rounded-full"
                data-testid="rcal-run-preview"
              >
                {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                {previewing ? "Scraping…" : "Preview cars"}
              </Button>
              {preview && (
                <span className="ml-3 text-xs text-neutral-500">
                  Found <strong>{preview.count}</strong> cars · vendor account{" "}
                  <strong>{preview.target_vendor_exists ? "exists" : "will be created"}</strong>
                </span>
              )}
            </div>
          </div>

          {/* Step 2: preview table */}
          {preview && preview.cars.length > 0 && (
            <div className="border border-border rounded-2xl overflow-hidden" data-testid="rcal-preview-table">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-neutral-50 border-b border-border">
                <Checkbox
                  checked={selected.size === selectableCount && selectableCount > 0}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid="rcal-select-all"
                />
                <span className="text-xs font-semibold text-neutral-700">
                  {selected.size} / {selectableCount} selected
                </span>
                <span className="ml-auto text-[11px] text-neutral-500">
                  Already-imported cars are skipped automatically.
                </span>
              </div>
              <ul className="divide-y divide-border max-h-[380px] overflow-y-auto">
                {preview.cars.map((c) => {
                  const disabled = c.already_imported;
                  const isSelected = selected.has(c.external_source_url);
                  return (
                    <li
                      key={c.external_source_url}
                      className={`flex items-center gap-3 p-3 ${disabled ? "bg-neutral-50/60 text-neutral-400" : "hover:bg-neutral-50"}`}
                      data-testid={`rcal-row-${c.external_source_url.split("/").filter(Boolean).slice(-1)[0] || "row"}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        onCheckedChange={() => !disabled && toggleOne(c.external_source_url)}
                      />
                      {c.primary_image && (
                        <img
                          src={c.primary_image}
                          alt=""
                          className="h-12 w-16 object-cover rounded-md shrink-0 bg-neutral-100"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name || `${c.make} ${c.model}`}</div>
                        <div className="text-[11px] text-neutral-500 flex flex-wrap gap-2 mt-0.5">
                          <span>{c.car_type}</span>
                          <span>· {c.transmission}</span>
                          <span>· {c.fuel_type}</span>
                          <span>· {c.seats} seats</span>
                          {(c.images || []).length > 1 && <span>· {c.images.length} photos</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-emerald-700 tabular-nums">€{Number(c.price_per_day || 0).toFixed(0)}<span className="text-[10px] text-neutral-400 font-normal">/day</span></div>
                        {disabled && (
                          <span className="text-[10px] text-amber-700 font-semibold uppercase tracking-wider">
                            Already imported
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Step 3: result summary */}
          {result && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2" data-testid="rcal-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="h-4 w-4" /> Import complete
              </div>
              <div className="text-sm text-neutral-700 flex flex-wrap gap-4">
                <span><strong>{(result.created || []).length}</strong> new cars</span>
                <span><strong>{(result.updated || []).length}</strong> updated</span>
                {(result.errors || []).length > 0 && (
                  <span className="text-rose-700"><strong>{result.errors.length}</strong> errors</span>
                )}
              </div>
              {result.vendor_temp_password && (
                <div className="text-xs bg-amber-100 border border-amber-200 rounded-md px-3 py-2 text-amber-900">
                  <AlertCircle className="inline h-3 w-3 mr-1" />
                  New vendor account created. Temporary password:
                  <code className="ml-2 font-mono bg-white px-2 py-0.5 rounded select-all">{result.vendor_temp_password}</code>
                </div>
              )}
              {(result.errors || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-rose-700 font-semibold">Show errors</summary>
                  <ul className="mt-2 space-y-1 text-rose-800">
                    {result.errors.map((e, i) => (
                      <li key={i}>• {e.name || e.url}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border bg-neutral-50/40 gap-2">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="rounded-full"
            data-testid="rcal-close"
          >
            Close
          </Button>
          <Button
            onClick={runImport}
            disabled={!preview || selected.size === 0 || importing}
            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white gap-1"
            data-testid="rcal-run-import"
          >
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {importing
              ? "Importing…"
              : selected.size > 0
                ? `Import ${selected.size} car${selected.size === 1 ? "" : "s"}`
                : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
